import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiActivity,
  FiCalendar,

  FiClipboard,
  FiClock,
  FiGrid,
  FiList,
  FiPhone,
  FiPlus,
  FiKey,
  FiMenu, FiLogOut,
  FiTrendingUp,
  FiUser,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import moment from "moment";
import favicon from "../assets/favicon.png";
import { BASE_URL, hospitalPost } from "../utils/hospitalApi";

const money = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const percent = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

const StatSkeleton = () => (
  <div className="h-stat h-skeleton-card">
    <span className="h-skeleton-box" />
    <div style={{ flex: 1 }}><span className="h-skeleton-line short" /><span className="h-skeleton-line value" /></div>
  </div>
);

const RecentSkeleton = () => (
  <div className="h-appointment-card h-skeleton-appointment">
    <div className="h-appointment-main"><div className="h-appointment-top"><span className="h-skeleton-line pill" /><span className="h-skeleton-line name" /><span className="h-skeleton-line status" /></div><div className="h-appointment-meta"><span className="h-skeleton-line meta" /><span className="h-skeleton-line meta" /><span className="h-skeleton-line meta" /></div></div>
    <span className="h-skeleton-line button" />
  </div>
);


const HospitalDashboard = () => {
  const navigate = useNavigate();
  const hospitalUserId = localStorage.getItem("hospital_user_id");
  const hospital = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("hospital_user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const profileImage = `${BASE_URL}assets/hospital/${hospital.foto || "user.png"}`;
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [now, setNow] = useState(new Date());

  const loadAppointments = useCallback(async () => {
    if (!hospitalUserId) return;
    setLoading(true);
    const res = await hospitalPost("hospital-get-appointments", { hospital_user_id: hospitalUserId });
    setLoading(false);
    if (res.data.status === 1) setAppointments(res.data.appointments || []);
    else toast.error(res.data.msg || "Unable to load appointments");
  }, [hospitalUserId]);

  useEffect(() => {
    if (!hospitalUserId) {
      navigate("/");
      return;
    }
    loadAppointments();
  }, [hospitalUserId, navigate, loadAppointments]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const stats = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    const uniquePatients = new Set(appointments.map((row) => row.patient_id).filter(Boolean)).size;
    const done = appointments.filter((row) => String(row.appointment_done || "").toUpperCase() === "YES").length;
    const pending = appointments.filter((row) => String(row.appointment_done || "").toUpperCase() !== "YES").length;
    return {
      total: appointments.length,
      today: appointments.filter((row) => row.appointment_date === today).length,
      done,
      pending,
      uniquePatients,
      received: appointments.reduce((sum, row) => sum + Number(row.received_amount || 0), 0),
      consult: appointments.reduce((sum, row) => sum + Number(row.consultation_fee || 0), 0),
      advance: appointments.reduce((sum, row) => sum + Number(row.advance_fee || 0), 0),
      balance: appointments.reduce((sum, row) => sum + Number(row.pending_fee || 0), 0),
    };
  }, [appointments]);

  const weeklyFlow = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => moment().subtract(6 - index, "days"));
    const maxCount = Math.max(1, ...days.map((day) => appointments.filter((row) => row.appointment_date === day.format("YYYY-MM-DD")).length));
    return days.map((day) => {
      const count = appointments.filter((row) => row.appointment_date === day.format("YYYY-MM-DD")).length;
      return { label: day.format("ddd"), date: day.format("DD MMM"), count, height: Math.max(10, Math.round((count / maxCount) * 100)) };
    });
  }, [appointments]);

  const recentAppointments = useMemo(() => {
    return [...appointments]
      .sort((a, b) => {
        const aDate = `${a.appointment_date || ""} ${a.appointment_time || ""}`;
        const bDate = `${b.appointment_date || ""} ${b.appointment_time || ""}`;
        return bDate.localeCompare(aDate);
      })
      .slice(0, 5);
  }, [appointments]);

  const visitRate = percent(stats.done, stats.total);
  const pendingRate = percent(stats.pending, stats.total);
  const collectionMax = Math.max(1, stats.consult, stats.advance, stats.received, stats.balance);
  const collectionRows = [
    { label: "Consultation", value: stats.consult, tone: "blue" },
    { label: "Advance", value: stats.advance, tone: "green" },
    { label: "Received", value: stats.received, tone: "purple" },
    { label: "Balance", value: stats.balance, tone: "red" },
  ];

  const logout = () => {
    localStorage.removeItem("hospital_user_id");
    localStorage.removeItem("hospital_user");
    navigate("/");
  };

  const statCards = [
    { label: "Total Appointments", value: stats.total, icon: <FiClipboard />, tone: "blue" },
    { label: "Today's Appointments", value: stats.today, icon: <FiCalendar />, tone: "teal" },
    { label: "Completed Visits", value: stats.done, icon: <FiUserCheck />, tone: "green" },
    { label: "Pending Visits", value: stats.pending, icon: <FiClock />, tone: "amber" },
    { label: "Patients", value: stats.uniquePatients, icon: <FiUsers />, tone: "purple" },
    { label: "Received Amount", value: money(stats.received), icon: <FaRupeeSign />, tone: "rose" },
  ];

  return (
    <div className="hospital-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        .hospital-shell { min-height:100vh; background:#edf3f7; font-family:'Nunito','DM Sans',sans-serif; color:#102033; display:flex; }
        .h-sidebar { width:118px; background:#07182e; color:#fff; min-height:100vh; position:sticky; top:0; align-self:flex-start; box-shadow:10px 0 28px rgba(15,23,42,.10); z-index:5; }
        .h-brand { height:68px; display:flex; align-items:center; justify-content:center; gap:8px; border-bottom:1px solid rgba(255,255,255,.09); }
        .h-brand img { width:34px; height:34px; object-fit:contain; }
        .h-nav { display:flex; flex-direction:column; padding:0; }
        .h-sidebar-logout { position:absolute; left:10px; right:10px; bottom:12px; }
        .h-sidebar-logout .h-btn { width:100%; min-height:42px; }
        .h-nav button { min-height:84px; border:0; background:transparent; color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:10px; font-size:13px; font-weight:900; border-bottom:1px solid rgba(255,255,255,.10); cursor:pointer; }
        .h-nav button.is-active { background:#fff; color:#0f172a; }
        .h-nav button svg { font-size:24px; color:#9ee7dc; }
        .h-nav button.is-active svg { color:#0f172a; }
        .h-nav button:hover { background:#fff; color:#0f172a; }
        .h-nav button:hover svg { color:#0f172a; }
        .h-main { flex:1; min-width:0; }
        .h-topbar { height:66px; background:#07182e; color:#fff; display:flex; align-items:center; justify-content:space-between; padding:0 20px; box-shadow:0 6px 18px rgba(15,23,42,.12); }
        .h-top-title { display:flex; align-items:center; gap:10px; font-size:20px; font-weight:900; letter-spacing:.4px; }
        .h-top-title img { width:34px; height:34px; object-fit:contain; }
        .h-profile-wrap { position:relative; } .h-profile { border:0; background:transparent; color:#fff; display:flex; align-items:center; gap:10px; cursor:pointer; padding:3px 0; } .h-profile-menu { position:absolute; right:0; top:54px; min-width:190px; background:#fff; color:#0f172a; border:1px solid #dbe3ef; border-radius:13px; box-shadow:0 18px 44px rgba(15,23,42,.18); padding:7px; z-index:20; } .h-profile-menu button { width:100%; border:0; background:transparent; color:#0f172a; display:flex; align-items:center; gap:9px; padding:10px 11px; border-radius:10px; font-size:12px; font-weight:900; text-align:left; cursor:pointer; } .h-profile-menu button:hover { background:#eff6ff; color:#0f766e; }
        .h-avatar { width:42px; height:42px; border-radius:50%; background:linear-gradient(135deg,#14b8a6,#0f766e); color:#fff; display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:900; border:3px solid rgba(255,255,255,.16); overflow:hidden; }
        .h-avatar img { width:100%; height:100%; object-fit:cover; }
        .h-profile-name { font-size:14px; font-weight:900; text-align:right; }
        .h-profile-sub { font-size:11px; color:#cbd5e1; text-align:right; }
        .h-content { padding:14px 14px 64px; }
        .h-footer { position:fixed; left:118px; right:0; bottom:0; height:46px; background:#fff; border-top:1px solid #e5e7eb; display:flex; align-items:center; justify-content:center; z-index:4; color:#8a94a6; font-size:12px; font-weight:700; box-shadow:0 -6px 18px rgba(15,23,42,.06); }
        .h-footer strong { color:#f05a00; font-weight:900; }
        .h-hero { min-height:170px; border-radius:24px; background:linear-gradient(120deg,#133761 0%,#0f766e 58%,#8b6f34 100%); color:#fff; padding:28px; display:flex; justify-content:space-between; gap:22px; align-items:center; box-shadow:0 18px 45px rgba(15,23,42,.16); margin-bottom:16px; }
        .h-badge { display:inline-flex; padding:7px 12px; border-radius:999px; background:rgba(255,255,255,.15); color:#dcfce7; font-size:11px; font-weight:900; letter-spacing:.8px; text-transform:uppercase; margin-bottom:12px; }
        .h-hero h1 { margin:0; font-size:34px; line-height:1.1; font-weight:900; letter-spacing:0; }
        .h-hero h1 span { color:#fde68a; }
        .h-hero p { margin:10px 0 0; color:#dbeafe; font-weight:800; }
        .h-clock-card { min-width:400px; background:rgba(255,255,255,.94); color:#0f172a; border-radius:22px; padding:8px 22px; box-shadow:0 16px 34px rgba(15,23,42,.16); }
        .h-clock-label { color:#8b5e14; font-size:11px; font-weight:900; text-transform:uppercase; letter-spacing:.9px; }
        .h-clock-time { font-size:25px; font-weight:900; margin-top:6px; }
        .h-clock-date { color:#64748b; font-size:13px; font-weight:700; margin-top:4px; }
        .h-live { display:inline-flex; align-items:center; gap:6px; margin-top:9px; font-size:12px; color:#15803d; font-weight:900; }
        .h-live:before { content:''; width:9px; height:9px; border-radius:50%; background:#10b981; display:block; }
        .h-section { background:rgba(255,255,255,.92); border:1px solid #e2e8f0; border-radius:22px; padding:18px 20px; box-shadow:0 12px 34px rgba(15,23,42,.08); margin-bottom:16px; }
        .h-section-kicker { color:#b7791f; font-size:11px; font-weight:900; text-transform:uppercase; letter-spacing:.9px; }
        .h-section-title-row { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; margin:5px 0 14px; }
        .h-section-title { font-size:23px; font-weight:900; color:#12213a; }
        .h-stat-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(210px,1fr)); gap:11px; }
        .h-stat { border:1px solid transparent; border-radius:14px; min-height:96px; padding:16px; display:flex; align-items:center; gap:14px; color:#fff; box-shadow:0 10px 24px rgba(15,23,42,.10); }
        .h-stat.blue { background:#2898e8; }
        .h-stat.teal { background:#0f9f8f; }
        .h-stat.green { background:#4caf50; }
        .h-stat.amber { background:#f8b814; }
        .h-stat.purple { background:#9c27b0; }
        .h-stat.rose { background:#fff; color:#07182e; border-color:#dbe3ef; }
        .h-stat-icon { width:52px; height:52px; border-radius:15px; display:flex; align-items:center; justify-content:center; font-size:24px; background:rgba(255,255,255,.86); color:#0f766e; flex:0 0 auto; }
        .h-stat.blue .h-stat-icon { color:#0284c7; }
        .h-stat.teal .h-stat-icon { color:#0f766e; }
        .h-stat.green .h-stat-icon { color:#16a34a; }
        .h-stat.amber .h-stat-icon { color:#d97706; }
        .h-stat.purple .h-stat-icon { color:#7c3aed; }
        .h-stat.rose .h-stat-icon { background:#fce7f3; color:#db2777; }
        .h-stat-label { color:inherit; opacity:.88; font-size:11px; font-weight:900; text-transform:uppercase; }
        .h-stat-value { font-size:24px; line-height:1.1; font-weight:900; margin-top:5px; color:inherit; }
        .h-visual-grid { display:grid; grid-template-columns:1.15fr .85fr; gap:16px; }
        .h-panel { background:#fff; border:1px solid #e2e8f0; border-radius:18px; padding:18px; box-shadow:0 10px 28px rgba(15,23,42,.07); min-height:270px; }
        .h-panel-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:16px; }
        .h-panel-title { display:flex; align-items:center; gap:8px; color:#0f172a; font-size:16px; font-weight:900; }
        .h-panel-title svg { color:#0f766e; }
        .h-panel-sub { color:#64748b; font-size:12px; font-weight:800; margin-top:4px; }
        .h-bars { height:180px; display:grid; grid-template-columns:repeat(7,1fr); gap:12px; align-items:end; padding:8px 4px 0; }
        .h-bar-cell { height:100%; display:flex; flex-direction:column; justify-content:flex-end; align-items:center; gap:8px; }
        .h-bar-track { width:100%; max-width:38px; height:140px; background:#edf3f7; border-radius:999px; display:flex; align-items:flex-end; overflow:hidden; border:1px solid #dbe3ef; }
        .h-bar-fill { width:100%; border-radius:999px; background:linear-gradient(180deg,#14b8a6,#0f766e); min-height:10px; }
        .h-bar-value { color:#0f172a; font-size:13px; font-weight:900; }
        .h-bar-label { color:#64748b; font-size:11px; font-weight:900; }
        .h-donut-wrap { display:flex; align-items:center; justify-content:center; gap:20px; flex-wrap:wrap; min-height:190px; }
        .h-donut { width:160px; height:160px; border-radius:50%; background:conic-gradient(#16a34a 0 ${visitRate}%, #f8b814 ${visitRate}% ${visitRate + pendingRate}%, #e5e7eb ${visitRate + pendingRate}% 100%); display:flex; align-items:center; justify-content:center; box-shadow:inset 0 0 0 18px #fff, 0 12px 30px rgba(15,23,42,.12); }
        .h-donut-center { width:96px; height:96px; border-radius:50%; background:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; box-shadow:0 8px 18px rgba(15,23,42,.08); }
        .h-donut-number { font-size:28px; font-weight:900; color:#16a34a; line-height:1; }
        .h-donut-text { font-size:10px; font-weight:900; color:#64748b; text-transform:uppercase; margin-top:4px; }
        .h-legend { min-width:170px; display:grid; gap:10px; }
        .h-legend-row { display:flex; align-items:center; justify-content:space-between; gap:12px; font-size:12px; font-weight:900; color:#334155; }
        .h-dot { width:10px; height:10px; border-radius:50%; display:inline-block; margin-right:7px; }
        .h-dot.green { background:#16a34a; } .h-dot.amber { background:#f8b814; } .h-dot.gray { background:#cbd5e1; }
        .h-money-list { display:grid; gap:14px; }
        .h-money-row { display:grid; grid-template-columns:120px 1fr auto; align-items:center; gap:12px; }
        .h-money-label { color:#334155; font-size:12px; font-weight:900; }
        .h-money-value { color:#0f172a; font-size:12px; font-weight:900; }
        .h-money-track { height:12px; border-radius:999px; background:#edf3f7; overflow:hidden; border:1px solid #e2e8f0; }
        .h-money-fill { height:100%; border-radius:999px; min-width:8px; }
        .h-money-fill.blue { background:#2898e8; } .h-money-fill.green { background:#16a34a; } .h-money-fill.purple { background:#9c27b0; } .h-money-fill.red { background:#dc2626; }
        .h-appointment-list { display:grid; gap:10px; }
        .h-appointment-card { display:grid; grid-template-columns:1fr auto; gap:14px; align-items:center; padding:13px 14px; border:1px solid #e2e8f0; border-radius:14px; background:linear-gradient(180deg,#fff,#f8fafc); box-shadow:0 8px 18px rgba(15,23,42,.05); }
        .h-appointment-main { min-width:0; }
        .h-appointment-top { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .h-patient-id { display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; background:#dcfce7; color:#15803d; border:1px solid #86efac; font-size:12px; font-weight:900; }
        .h-appointment-name { color:#0f172a; font-size:14px; font-weight:900; }
        .h-appointment-meta { display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-top:8px; color:#64748b; font-size:12px; font-weight:800; }
        .h-appointment-meta span { display:inline-flex; align-items:center; gap:5px; }
        .h-status { display:inline-flex; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:900; text-transform:uppercase; }
        .h-status.done { background:#dcfce7; color:#15803d; }
        .h-status.pending { background:#fef3c7; color:#b45309; }
        .h-empty-state { border:1px dashed #cbd5e1; border-radius:14px; padding:24px; text-align:center; color:#64748b; font-size:13px; font-weight:900; background:#f8fafc; }
        .h-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; border:none; border-radius:9px; padding:9px 13px; font-size:12px; font-weight:900; cursor:pointer; white-space:nowrap; }
        .h-btn-primary { background:#0f766e; color:#fff; }
        .h-btn-soft { background:#e6fffb; color:#0f766e; }
        .h-btn-danger { background:#fee2e2; color:#dc2626; }
        .h-loading { color:#64748b; font-weight:900; font-size:13px; margin-top:14px; }
        @media (max-width:1180px) { .h-visual-grid { grid-template-columns:1fr; } }
        @media (max-width:980px) { .h-footer { left:0; } .h-sidebar-logout { position:static; flex:0 0 auto; padding:10px; min-width:112px; } .hospital-shell { display:block; } .h-sidebar { position:relative; width:100%; min-height:auto; display:flex; align-items:center; overflow-x:auto; } .h-brand { flex:0 0 96px; width:96px; height:66px; border-bottom:0; margin-bottom:0; } .h-nav { flex:1; flex-direction:row; overflow-x:auto; } .h-nav button { min-height:66px; width:auto; min-width:104px; border-bottom:0; border-left:1px solid rgba(255,255,255,.08); } .h-hero { flex-direction:column; align-items:flex-start; } .h-clock-card { min-width:0; width:100%; } .h-topbar { height:auto; padding:12px; } }
        @media (max-width:640px) { .h-content { padding:10px 10px 64px; } .h-hero { padding:20px; border-radius:18px; } .h-hero h1 { font-size:26px; } .h-section { border-radius:16px; padding:14px; } .h-section-title { font-size:20px; } .h-profile-name, .h-profile-sub { display:none; } .h-money-row { grid-template-columns:1fr; gap:6px; } }
        .h-menu-toggle { display:none; width:38px; height:38px; border:0; border-radius:10px; background:#e6fffb; color:#0f766e; align-items:center; justify-content:center; font-size:20px; cursor:pointer; }
        .h-sidebar-backdrop { display:none; }
        @media (max-width:980px) {
          .h-menu-toggle { display:inline-flex; flex:0 0 auto; }
          .h-topbar { gap:10px; }
          .h-top-title { flex:1; min-width:0; font-size:17px; }
          .hospital-shell { display:block; }
          .h-main { min-width:0; }
          .h-footer { left:0 !important; }
          .h-sidebar { position:fixed !important; left:0; top:0; bottom:0; width:244px !important; min-height:100vh !important; transform:translateX(-105%); transition:transform .22s ease; display:block !important; overflow:auto !important; z-index:60; box-shadow:18px 0 42px rgba(7,24,46,.28); }
          .h-sidebar.is-open { transform:translateX(0); }
          .h-sidebar-backdrop { display:block; position:fixed; inset:0; border:0; background:rgba(7,24,46,.55); z-index:55; }
          .h-brand { width:auto !important; height:76px !important; flex:0 0 auto !important; border-bottom:1px solid rgba(255,255,255,.09) !important; margin:0 !important; }
          .h-nav { flex:0 0 auto !important; flex-direction:column !important; overflow:visible !important; }
          .h-nav button { min-height:62px !important; min-width:0 !important; width:100% !important; flex-direction:row !important; justify-content:flex-start !important; gap:12px !important; padding:0 18px !important; border-left:0 !important; border-bottom:1px solid rgba(255,255,255,.10) !important; }
          .h-nav button svg { font-size:20px !important; }
          .h-sidebar-logout { position:static !important; padding:14px !important; min-width:0 !important; }
          .h-sidebar-logout .h-btn { width:100% !important; min-height:42px; }
        }
      `}</style>

      <aside className={`h-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="h-brand">
          <img src={favicon} alt="Sat Kartar" />
        </div>
        <nav className="h-nav">
          <button className="is-active" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><FiGrid /> Dashboard</button>
          <button type="button" onClick={() => navigate("/patient-entry")}><FiPlus /> Patient Entry</button>
          <button type="button" onClick={() => navigate("/appointments")}><FiList /> Appointment List</button>
                  <button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button>
</nav>
        <div className="h-sidebar-logout"><button className="h-btn h-btn-danger" onClick={logout}><FiLogOut size={14} /> Logout</button></div>
      </aside>
      {sidebarOpen ? <button className="h-sidebar-backdrop" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} /> : null}

      <main className="h-main">
        <header className="h-topbar">
          <button className="h-menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}><FiMenu /></button>
          <div className="h-top-title"><span style={{ color: "#ff4b21" }}>SAT KARTAR</span><span style={{ color: "#61a5ff" }}>SANJEEVAN</span></div>
          <div className="h-profile-wrap" onMouseLeave={() => setProfileOpen(false)}>
            <button className="h-profile" type="button" onMouseEnter={() => setProfileOpen(true)} onClick={() => setProfileOpen(true)}>
              <div><div className="h-profile-name">{hospital.hospital_name || "Hospital User"}</div><div className="h-profile-sub">Hospital Panel</div></div>
              <div className="h-avatar"><img src={profileImage} alt="Profile" onError={(e) => { e.currentTarget.src = `${BASE_URL}assets/hospital/user.png`; }} /></div>
              
            </button>
            {profileOpen ? <div className="h-profile-menu"><button type="button" onClick={() => navigate("/change-password")}><FiKey /> Change Password</button><button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button></div> : null}
          </div>
        </header>

        <div className="h-content">
          <section className="h-hero">
            <div>
              <div className="h-badge">Hospital Panel</div>
              <h1>Welcome back, <span>{hospital.hospital_name || "Hospital"}</span></h1>
              <p>Manage patient appointments, treatment updates, and received payments.</p>
            </div>
            <div className="h-clock-card">
              <div className="h-clock-label">Live Clock</div>
              <div className="h-clock-time">{moment(now).format("hh:mm:ss A")}</div>
              <div className="h-clock-date">{moment(now).format("dddd, DD MMM YYYY")}</div>
              <div className="h-live">Live time</div>
            </div>
          </section>

          <section className="h-section">
            <div className="h-section-kicker">Hospital Overview</div>
            <div className="h-section-title-row">
              <div className="h-section-title">Patient appointment momentum</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><button className="h-btn h-btn-primary" type="button" onClick={() => navigate("/patient-entry")}><FiPlus size={14} /> Patient Entry</button><button className="h-btn h-btn-soft" type="button" onClick={() => navigate("/appointments")}><FiList size={14} /> Appointment List</button></div>
            </div>
            <div className="h-stat-grid">
              {loading && !appointments.length ? Array.from({ length: 6 }).map((_, index) => <StatSkeleton key={index} />) : statCards.map((card) => (
                <div className={`h-stat ${card.tone}`} key={card.label}>
                  <div className="h-stat-icon">{card.icon}</div>
                  <div><div className="h-stat-label">{card.label}</div><div className="h-stat-value">{card.value}</div></div>
                </div>
              ))}
            </div>
            {loading ? <div className="h-loading">Refreshing dashboard...</div> : null}
          </section>
          <section className="h-visual-grid">
            <div className="h-panel">
              <div className="h-panel-head">
                <div>
                  <div className="h-panel-title"><FiTrendingUp /> Weekly Appointment Flow</div>
                  <div className="h-panel-sub">Last 7 days booking pattern</div>
                </div>
              </div>
              <div className="h-bars">
                {weeklyFlow.map((day) => (
                  <div className="h-bar-cell" key={day.date} title={day.date}>
                    <div className="h-bar-value">{day.count}</div>
                    <div className="h-bar-track"><div className="h-bar-fill" style={{ height: `${day.height}%` }} /></div>
                    <div className="h-bar-label">{day.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-panel">
              <div className="h-panel-head">
                <div>
                  <div className="h-panel-title"><FiActivity /> Visit Conversion</div>
                  <div className="h-panel-sub">Completed and pending appointment ratio</div>
                </div>
              </div>
              <div className="h-donut-wrap">
                <div className="h-donut">
                  <div className="h-donut-center">
                    <div className="h-donut-number">{visitRate}%</div>
                    <div className="h-donut-text">Completed</div>
                  </div>
                </div>
                <div className="h-legend">
                  <div className="h-legend-row"><span><span className="h-dot green" />Completed</span><b>{stats.done}</b></div>
                  <div className="h-legend-row"><span><span className="h-dot amber" />Pending</span><b>{stats.pending}</b></div>
                  <div className="h-legend-row"><span><span className="h-dot gray" />Total</span><b>{stats.total}</b></div>
                </div>
              </div>
            </div>

            <div className="h-panel" style={{ gridColumn: "1 / -1" }}>
              <div className="h-panel-head">
                <div>
                  <div className="h-panel-title"><FaRupeeSign /> Financial Snapshot</div>
                  <div className="h-panel-sub">Consultation, advance, received and balance comparison</div>
                </div>
              </div>
              <div className="h-money-list">
                {collectionRows.map((row) => (
                  <div className="h-money-row" key={row.label}>
                    <div className="h-money-label">{row.label}</div>
                    <div className="h-money-track"><div className={`h-money-fill ${row.tone}`} style={{ width: `${Math.max(2, percent(row.value, collectionMax))}%` }} /></div>
                    <div className="h-money-value">{money(row.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="h-section mt-3">
            <div className="h-section-title-row">
              <div>
                <div className="h-section-kicker">New Appointments</div>
                <div className="h-section-title">Latest appointment queue</div>
              </div>
              <button className="h-btn h-btn-soft" type="button" onClick={() => navigate("/appointments")}><FiList size={14} /> View All</button>
            </div>
            <div className="h-appointment-list">
              {loading && !recentAppointments.length ? Array.from({ length: 4 }).map((_, index) => <RecentSkeleton key={index} />) : recentAppointments.length ? recentAppointments.map((row) => {
                const isDone = String(row.appointment_done || "").toUpperCase() === "YES";
                return (
                  <div className="h-appointment-card" key={row.hospital_appointment_id}>
                    <div className="h-appointment-main">
                      <div className="h-appointment-top">
                        <span className="h-patient-id">{row.patient_id || "-"}</span>
                        <span className="h-appointment-name">{row.customer_name || "Patient"}</span>
                        <span className={`h-status ${isDone ? "done" : "pending"}`}>{isDone ? "Done" : "Pending"}</span>
                      </div>
                      <div className="h-appointment-meta">
                        <span><FiPhone /> {row.phone_number || "-"}</span>
                        <span><FiCalendar /> {row.appointment_date ? moment(row.appointment_date).format("DD MMM YYYY") : "-"} {row.appointment_time || ""}</span>
                        <span>{row.city_area || "-"}</span>
                      </div>
                    </div>
                    <button className="h-btn h-btn-primary" type="button" onClick={() => navigate("/appointments")}>Update</button>
                  </div>
                );
              }) : <div className="h-empty-state">No new appointments found</div>}
            </div>
          </section>
        </div>
        <footer className="h-footer"><strong>&copy; 2026 Sat Kartar Life Limited.</strong>&nbsp; All Rights Reserved</footer>
      </main>
    </div>
  );
};

export default HospitalDashboard;




















